import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Shield, BookOpen, Award, Brain, FileText, Sparkles, Building2,
         Lock, Unlock, UserPlus, ChevronRight, Loader2, AlertTriangle,
         Activity, Eye, Wrench, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('nexus_token');
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const MODULE_META = [
  { key: 'academy', label: 'Nexus Academy', icon: BookOpen },
  { key: 'certification', label: 'Certification Center', icon: Award },
  { key: 'skip_simulator', label: 'Skip Trace Simulator', icon: Brain },
  { key: 'surveillance_simulator', label: 'Surveillance Simulator', icon: Brain },
  { key: 'report_lab', label: 'Report Writing Lab', icon: FileText },
  { key: 'ai_coach', label: 'AI Investigator Coach', icon: Sparkles },
  { key: 'university', label: 'Nexus University', icon: Building2 },
];

const UserDetailPanel = ({ userId, courses, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [pickedCourses, setPickedCourses] = useState({});
  const [assigning, setAssigning] = useState(false);
  // Sync diagnostic / View-As / Repair tooling (Christopher Hope bug fix)
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [viewAs, setViewAs] = useState(null);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const refetch = async () => {
    const r = await apiCall(`/api/tams/users/${userId}`);
    setDetail(r);
    setPerms(r.user.training_permissions);
  };
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiCall(`/api/tams/users/${userId}`);
        if (!alive) return;
        setDetail(r);
        setPerms(r.user.training_permissions);
      } catch (e) { if (alive) toast.error(e.message); }
    })();
    return () => { alive = false; };
  }, [userId]);

  const setModule = (key, val) => {
    setPerms({ ...perms, modules: { ...(perms?.modules || {}), [key]: val } });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await apiCall(`/api/tams/users/${userId}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify({
          training_center_access: perms.training_center_access,
          modules: perms.modules,
          ai_coach_level: perms.ai_coach_level,
        }),
      });
      toast.success('Permissions saved');
      onSaved?.();
      await refetch();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const submitAssign = async () => {
    const ids = Object.keys(pickedCourses).filter(k => pickedCourses[k]);
    if (ids.length === 0) { toast.error('Pick at least one course'); return; }
    setAssigning(true);
    try {
      const r = await apiCall(`/api/tams/users/${userId}/bulk-assign`, {
        method: 'POST',
        body: JSON.stringify({ course_ids: ids, due_days: 30, required: true }),
      });
      const failed = r.results.filter(x => !x.success);
      if (failed.length) toast.error(`${failed.length} assignment(s) failed`);
      else toast.success(`Assigned ${ids.length} course${ids.length === 1 ? '' : 's'}`);
      setPickedCourses({}); setAssignOpen(false);
      await refetch();
    } catch (e) { toast.error(e.message); }
    finally { setAssigning(false); }
  };

  // --- Sync diagnostic: surface exactly what the investigator can see ---
  const runDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const d = await apiCall(`/api/tams/users/${userId}/sync-diagnostic`);
      setDiagnostic(d);
      toast.success(`Sync diagnostic loaded · status ${d.sync_status}`);
    } catch (e) { toast.error(e.message); }
    finally { setDiagLoading(false); }
  };

  // --- View As Investigator: load read-only snapshot of their Training Center ---
  const openViewAs = async () => {
    try {
      const v = await apiCall(`/api/tams/users/${userId}/view-as`);
      setViewAs(v);
      setViewAsOpen(true);
    } catch (e) { toast.error(e.message); }
  };

  // --- Repair Assignments: self-heal sync state ---
  const repairAssignments = async () => {
    if (!window.confirm('Repair Assignments will force-enable Training Center access, restore default module permissions, and re-apply any required (auto-assign-on-hire) courses for this user. Continue?')) return;
    setRepairing(true);
    try {
      const r = await apiCall(`/api/tams/users/${userId}/repair-assignments`, { method: 'POST' });
      toast.success(`Repair complete · ${r.assigned_courses_now} courses now visible`);
      // Auto-refresh diagnostic + detail
      setDiagnostic(null);
      await refetch();
      await runDiagnostic();
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    finally { setRepairing(false); }
  };

  if (!detail || !perms) {
    return <div className="text-slate-400 italic text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading user…</div>;
  }
  const u = detail.user;

  return (
    <Card className="bg-[#131A2A] border-[#1E293B]" data-testid="tams-detail-panel">
      <CardContent className="p-5 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">{u.role.replace(/_/g, ' ')}</p>
            <h3 className="text-lg font-bold text-white truncate">{u.name || u.username}</h3>
            <p className="text-xs text-slate-400">{u.email} · {u.username}</p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} className="border-slate-600 !text-slate-200 hover:bg-slate-800" data-testid="tams-close-detail">Close</Button>
        </div>

        {/* Sync Diagnostic / View-As / Repair toolbar (Critical Bug Fix) */}
        <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 space-y-3" data-testid="tams-sync-toolbar">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Activity className="w-4 h-4 text-cyan-300 flex-shrink-0" />
              <p className="text-xs font-semibold text-white">Assignment Sync Diagnostics</p>
              {diagnostic && (
                <Badge className={`text-[10px] font-mono uppercase border ${diagnostic.sync_status === 'OK' ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40' : 'bg-rose-500/20 text-rose-200 border-rose-500/40'}`} data-testid="tams-sync-status">
                  {diagnostic.sync_status}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" onClick={runDiagnostic} disabled={diagLoading}
                className="border-cyan-500/50 !text-cyan-200 hover:bg-cyan-500/10 h-7 text-xs" data-testid="tams-run-diagnostic">
                {diagLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Run Diagnostic
              </Button>
              <Button size="sm" variant="outline" onClick={openViewAs}
                className="border-amber-500/50 !text-amber-200 hover:bg-amber-500/10 h-7 text-xs" data-testid="tams-view-as">
                <Eye className="w-3 h-3 mr-1" /> View As Investigator
              </Button>
              <Button size="sm" onClick={repairAssignments} disabled={repairing}
                className="bg-rose-500 hover:bg-rose-400 !text-white h-7 text-xs" data-testid="tams-repair">
                {repairing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wrench className="w-3 h-3 mr-1" />}
                Repair Assignments
              </Button>
            </div>
          </div>

          {diagnostic && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-[11px]" data-testid="tams-diagnostic-grid">
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Auth User ID</p>
                <p className="text-cyan-200 font-mono truncate">{diagnostic.authenticated_user_id}</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">TC Access</p>
                <p className={`font-mono ${diagnostic.training_center_access ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {diagnostic.training_center_access ? 'ENABLED' : 'BLOCKED'} <span className="text-slate-500">({diagnostic.training_center_access_source})</span>
                </p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Assigned Courses</p>
                <p className="text-white font-mono">{diagnostic.counts.assigned_courses}</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Completed</p>
                <p className="text-white font-mono">{diagnostic.counts.completed_courses}</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Active Certs</p>
                <p className="text-white font-mono">{diagnostic.counts.active_certifications}</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Exam Attempts</p>
                <p className="text-white font-mono">{diagnostic.counts.exam_attempts}</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5 col-span-2">
                <p className="text-slate-500 uppercase tracking-wider text-[9px]">Last Sync Event</p>
                <p className="text-slate-200 font-mono truncate text-[10px]">{diagnostic.last_assignment ? `${diagnostic.last_assignment.course_title} @ ${diagnostic.last_assignment.assigned_at?.slice(0,16).replace('T', ' ')}` : 'none'}</p>
              </div>
              <div className="col-span-full bg-slate-900/60 border border-slate-700 rounded px-2 py-1.5">
                <p className="text-slate-500 uppercase tracking-wider text-[9px] mb-1">Module Access</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(diagnostic.module_access || {}).map(([m, ok]) => (
                    <span key={m} className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-slate-700 bg-slate-900 text-slate-500'}`}>
                      {ok ? <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" /> : <XCircle className="w-2.5 h-2.5 inline mr-0.5" />}
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              {diagnostic.sync_status_detail && (
                <div className="col-span-full bg-slate-900/60 border-l-2 border-cyan-400 px-2 py-1.5">
                  <p className="text-[10px] text-slate-300 italic">{diagnostic.sync_status_detail}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Master toggle */}
        <div className="rounded-md border-2 border-amber-500/30 bg-amber-500/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="tams-master-toggle-row">
          <div className="min-w-0 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-300 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white">Nexus Training Center access</p>
              <p className="text-[11px] text-slate-400">Disables sidebar, dashboard card, and direct URL access. Historical records preserved.</p>
            </div>
          </div>
          <Switch checked={perms.training_center_access}
                  onCheckedChange={(v) => setPerms({ ...perms, training_center_access: v })}
                  data-testid="tams-toggle-trc-access" />
        </div>

        {/* Module toggles */}
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Module access</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tams-modules-grid">
            {MODULE_META.map(m => {
              const Icon = m.icon;
              const enabled = !!perms.modules?.[m.key];
              return (
                <div key={m.key} className={`rounded-md border ${enabled ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700 bg-slate-900/40'} p-2.5 flex items-center justify-between gap-3`} data-testid={`tams-mod-row-${m.key}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${enabled ? 'text-emerald-300' : 'text-slate-500'}`} />
                    <p className={`text-xs ${enabled ? 'text-white' : 'text-slate-400'} truncate`}>{m.label}</p>
                  </div>
                  <Switch checked={enabled} onCheckedChange={(v) => setModule(m.key, v)} data-testid={`tams-mod-toggle-${m.key}`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Coach level */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">AI Coach level</p>
            <Select value={perms.ai_coach_level} onValueChange={(v) => setPerms({ ...perms, ai_coach_level: v })}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="tams-coach-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic — training + software Q&A</SelectItem>
                <SelectItem value="advanced">Advanced — report writing + simulation coaching</SelectItem>
                <SelectItem value="instructor">Instructor — can generate exams + learning paths</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={saveAll} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="tams-save-perms">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Save permissions'}
            </Button>
          </div>
        </div>

        {/* Assignments */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Course assignments ({detail.assignments.length})</p>
            <Button size="sm" onClick={() => setAssignOpen(true)} className="bg-slate-800 hover:bg-slate-700 !text-white border border-slate-600" data-testid="tams-open-assign">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign
            </Button>
          </div>
          {detail.assignments.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No courses assigned yet.</p>
          ) : (
            <div className="space-y-1.5">
              {detail.assignments.map(a => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded border border-slate-700 bg-slate-900/40 p-2 text-xs" data-testid={`tams-assignment-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{a.course_title}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{a.course_id} · due {a.due_date}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Badge className={`text-[10px] font-mono uppercase ${a.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'bg-slate-800 text-slate-300 border border-slate-600'}`}>{a.status}</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500/40 !text-amber-300 hover:bg-amber-500/15 text-[10px] h-7 px-2"
                      onClick={async () => {
                        if (!window.confirm(`Reset ${a.course_id} exam attempts for this user? They'll be able to retake immediately.`)) return;
                        try {
                          const r = await apiCall(`/api/training-center/admin/users/${userId}/courses/${a.course_id}/reset-exam`, {
                            method: 'POST',
                            body: JSON.stringify({ wipe_all_attempts: false, clear_session: true }),
                          });
                          toast.success(`Reset OK · ${r.attempts_removed} attempt(s) cleared`);
                        } catch (e) { toast.error(e.message); }
                      }}
                      data-testid={`tams-reset-exam-${a.course_id}`}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Reset Exam
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active certs */}
        {detail.certs?.length > 0 && (
          <div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Active certifications</p>
            <div className="space-y-1.5">
              {detail.certs.map(c => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs" data-testid={`tams-cert-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{c.cert_title}</p>
                    <p className="text-[10px] text-emerald-200 font-mono">expires {new Date(c.expires_at).toLocaleDateString()}</p>
                  </div>
                  <Award className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Assign-course dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-[#0F1B33] border-amber-500/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Assign courses to {u.name || u.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto py-2">
            {courses.map(c => {
              const already = detail.assignments.some(a => a.course_id === c.id);
              return (
                <label key={c.id} className={`flex items-start gap-2 rounded border p-2 text-xs ${already ? 'border-slate-700 bg-slate-900/30 text-slate-500' : pickedCourses[c.id] ? 'border-amber-500/50 bg-amber-500/10' : 'border-slate-700 bg-slate-900/40 hover:bg-slate-800/40 cursor-pointer'}`} data-testid={`tams-pick-${c.id}`}>
                  <input type="checkbox" className="mt-1 accent-amber-400" disabled={already} checked={!!pickedCourses[c.id]} onChange={(e) => setPickedCourses({ ...pickedCourses, [c.id]: e.target.checked })} />
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium ${already ? 'text-slate-500' : 'text-white'}`}>{c.code} — {c.title}</p>
                    <p className="text-[10px] text-slate-500">{c.category.replace(/_/g, ' ')} · {c.estimated_hours}h</p>
                  </div>
                  {already && <span className="text-[10px] font-mono text-slate-500 self-center">Already</span>}
                </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-slate-600 !text-slate-200">Cancel</Button>
            <Button onClick={submitAssign} disabled={assigning} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="tams-confirm-assign">
              {assigning ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Assigning…</> : 'Assign selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View As Investigator dialog — read-only snapshot of the user's Training Center view */}
      <Dialog open={viewAsOpen} onOpenChange={setViewAsOpen}>
        <DialogContent className="bg-[#0F1B33] border-amber-500/30 text-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="tams-view-as-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-300" />
              Viewing as {viewAs?.viewing_as?.name} — Investigator Training Center
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Read-only snapshot of what this investigator sees in their Training Center — stats, assignments, and certifications.
            </DialogDescription>
          </DialogHeader>
          {viewAs && (
            <div className="space-y-4 mt-3">
              <div className="rounded border border-slate-700 bg-slate-900/60 p-3 text-xs">
                <p className="text-slate-400">Role: <span className="text-white font-mono">{viewAs.viewing_as.role}</span> · Training Center Access: <span className={viewAs.viewing_as.training_center_access ? 'text-emerald-300 font-mono' : 'text-rose-300 font-mono'}>{viewAs.viewing_as.training_center_access ? 'ENABLED' : 'BLOCKED'}</span></p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="tams-view-as-stats">
                {[
                  { l: 'Courses Assigned', v: viewAs.stats.courses_assigned, c: 'text-amber-300' },
                  { l: 'Completed', v: viewAs.stats.courses_completed, c: 'text-emerald-300' },
                  { l: 'Certifications', v: viewAs.stats.certifications_earned, c: 'text-cyan-300' },
                  { l: 'Rank', v: viewAs.stats.current_rank, c: 'text-white' },
                ].map((s, i) => (
                  <div key={i} className="bg-slate-900/60 border border-slate-700 rounded p-2.5">
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">{s.l}</p>
                    <p className={`text-lg font-bold ${s.c}`}>{s.v}</p>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Assignments ({viewAs.assignments.length})</p>
                {viewAs.assignments.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No assignments visible to this investigator.</p>
                ) : (
                  <div className="space-y-1.5">
                    {viewAs.assignments.map(a => (
                      <div key={a.id} className="flex items-center justify-between gap-2 rounded border border-slate-700 bg-slate-900/40 p-2 text-xs">
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{a.course_title}</p>
                          <p className="text-[10px] text-slate-500 font-mono">{a.course_id} · due {a.due_date}</p>
                        </div>
                        <Badge className={`text-[10px] font-mono uppercase ${a.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'bg-slate-800 text-slate-300 border border-slate-600'}`}>{a.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewAsOpen(false)} className="border-slate-600 !text-slate-200" data-testid="tams-view-as-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const TamsPanel = () => {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Course Health (Iter91 follow-up: production unlock for empty exam pools)
  const [generatingFor, setGeneratingFor] = useState(null);  // course_id of in-flight exam-bank job
  const [generatingLessonsFor, setGeneratingLessonsFor] = useState(null);  // course_id of in-flight lesson-body job

  const doFetch = async (q) => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        apiCall(`/api/tams/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
        apiCall('/api/training-center/courses'),
      ]);
      setUsers(u.users || []);
      setCourses(c.courses || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  const refresh = (q = query) => doFetch(q);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [u, c] = await Promise.all([
          apiCall('/api/tams/users'),
          apiCall('/api/training-center/courses'),
        ]);
        if (!alive) return;
        setUsers(u.users || []);
        setCourses(c.courses || []);
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => users, [users]);

  return (
    <div className="space-y-4" data-testid="tams-panel">
      {/* Course Health — exam pool size + one-click "Generate" via GPT-5.2.
          Fixes the production "0 questions / blank exam" lockout without
          needing any DB scripts. */}
      <Card className="bg-[#131A2A] border-[#1E293B]" data-testid="tams-course-health">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-cyan-300" />
            <h3 className="text-base font-bold text-white">Course Health · Exam Banks</h3>
            <Badge className="text-[10px] font-mono uppercase bg-cyan-500/10 text-cyan-200 border border-cyan-500/30">
              {courses.filter(c => (c.final_exam_pool_size ?? c.exam_pool_size ?? 0) >= 50).length}/{courses.length} ready
            </Badge>
          </div>
          {courses.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No courses loaded.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="tams-course-health-grid">
              {courses.map(c => {
                const size = c.final_exam_pool_size ?? c.exam_pool_size ?? 0;
                const ok = size >= 50;
                const busy = generatingFor === c.id;
                return (
                  <div key={c.id} className={`rounded border p-2.5 text-xs ${
                    ok ? 'border-emerald-500/30 bg-emerald-500/5'
                       : 'border-amber-500/40 bg-amber-500/5'
                  }`} data-testid={`tams-course-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{c.title}</p>
                        <p className="text-[10px] text-slate-500 font-mono">{c.id}</p>
                      </div>
                      <Badge className={`text-[10px] font-mono ${
                        ok ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                           : 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                      }`} data-testid={`tams-course-pool-size-${c.id}`}>
                        {size} q
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm(`Generate ${ok ? 'additional' : '50'} exam questions for ${c.id} via GPT-5.2? This takes ~2 minutes.`)) return;
                        setGeneratingFor(c.id);
                        try {
                          const r = await apiCall(
                            `/api/training-center/admin/courses/${c.id}/generate-exam-bank`,
                            { method: 'POST', body: JSON.stringify({ target_n: 50, overwrite: false }) },
                          );
                          toast.success(`${c.id}: pool now ${r.pool_size} questions (+${r.questions_generated})`);
                          // refresh courses list so the new size shows up
                          const fresh = await apiCall('/api/training-center/courses');
                          setCourses(Array.isArray(fresh) ? fresh : fresh.courses || []);
                        } catch (e) {
                          toast.error(`${c.id}: ${e.message}`);
                        } finally {
                          setGeneratingFor(null);
                        }
                      }}
                      disabled={busy}
                      className={`w-full h-7 text-[11px] font-semibold ${
                        ok ? 'bg-slate-800 hover:bg-slate-700 !text-slate-200 border border-slate-600'
                           : 'bg-amber-500 hover:bg-amber-400 !text-slate-900'
                      }`}
                      data-testid={`tams-generate-exam-${c.id}`}
                    >
                      {busy
                        ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…</>)
                        : ok
                          ? (<><RefreshCw className="w-3 h-3 mr-1" /> Add more questions</>)
                          : (<><Sparkles className="w-3 h-3 mr-1" /> Generate exam bank</>)}
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm(`Generate ~1500-word lesson bodies for every empty lesson on ${c.id} via GPT-5.2? This takes ~30-60 seconds per lesson and may run for several minutes.`)) return;
                        setGeneratingLessonsFor(c.id);
                        try {
                          const r = await apiCall(
                            `/api/training-center/admin/courses/${c.id}/generate-lesson-bodies`,
                            { method: 'POST', body: JSON.stringify({ target_words: 1500, overwrite: false }) },
                          );
                          if (r.lessons_generated > 0) {
                            toast.success(`${c.id}: filled ${r.lessons_generated} lesson${r.lessons_generated !== 1 ? 's' : ''} (${r.lessons_total} total)`);
                          } else {
                            toast.message(`${c.id}: ${r.note || 'No lessons needed generation.'}`);
                          }
                        } catch (e) {
                          toast.error(`${c.id}: ${e.message}`);
                        } finally {
                          setGeneratingLessonsFor(null);
                        }
                      }}
                      disabled={generatingLessonsFor === c.id}
                      className="w-full h-7 text-[11px] font-semibold mt-1 bg-slate-800 hover:bg-slate-700 !text-slate-200 border border-slate-600"
                      data-testid={`tams-generate-lessons-${c.id}`}
                    >
                      {generatingLessonsFor === c.id
                        ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Writing lessons…</>)
                        : (<><FileText className="w-3 h-3 mr-1" /> Generate lesson bodies</>)}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* User list */}
      <div className="lg:col-span-5">
        <Card className="bg-[#131A2A] border-[#1E293B]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-amber-300" />
              <h3 className="text-base font-bold text-white">TAMS · User access</h3>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={query} onChange={e => setQuery(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && refresh(query)}
                       placeholder="Search by name, username, or email"
                       className="bg-slate-900 border-slate-700 text-white pl-8" data-testid="tams-search" />
              </div>
              <Button size="sm" onClick={() => refresh(query)} className="bg-slate-800 hover:bg-slate-700 !text-white border border-slate-600" data-testid="tams-search-btn">Go</Button>
            </div>

            {loading && <p className="text-slate-400 italic text-sm">Loading…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-slate-500 italic text-sm">No matching users.</p>
            )}

            <div className="space-y-1.5 max-h-[64vh] overflow-y-auto" data-testid="tams-user-list">
              {filtered.map(u => {
                const access = u.training_permissions?.training_center_access;
                return (
                  <button key={u.id} onClick={() => setSelectedId(u.id)}
                          className={`w-full text-left rounded border p-2.5 transition flex items-center gap-2.5 ${
                            selectedId === u.id ? 'border-amber-500/60 bg-amber-500/10'
                                                 : 'border-slate-700 bg-slate-900/40 hover:bg-slate-800/40'
                          }`}
                          data-testid={`tams-user-${u.id}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${access ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate font-medium">{u.name || u.username}</p>
                      <p className="text-[10px] text-slate-500 font-mono truncate">{u.role} · {u.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400">
                      <span title="Assigned">{u.stats?.assigned ?? 0}A</span>
                      <span title="Completed">{u.stats?.completed ?? 0}C</span>
                      <span title="Active certs">{u.stats?.certs ?? 0}★</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail panel */}
      <div className="lg:col-span-7">
        {selectedId ? (
          <UserDetailPanel userId={selectedId} courses={courses}
                            onClose={() => setSelectedId(null)} onSaved={refresh} />
        ) : (
          <Card className="bg-[#131A2A] border-dashed border-2 border-slate-700">
            <CardContent className="p-10 text-center">
              <Lock className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-white">Select a user to manage their training access</p>
              <p className="text-xs text-slate-400 mt-2 max-w-md mx-auto">
                Toggle Training Center access, set per-module permissions (Academy, Certifications, Simulators, Report Lab, AI Coach, University), set AI Coach level, and assign courses in bulk. All changes are audited.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
};

export default TamsPanel;
